/**
 * COSO Compliance Slice
 * 
 * Redux state management for EX8 (COSO Compliance)
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { resetReduxState } from '../actions';
import type { ParsedCOSONote, ParsedCOSODocument } from '../../services/cosoComplianceService';

// ========================================
// State Interface
// ========================================

interface COSOComplianceState {
  // Notes (principle content from API)
  notes: ParsedCOSONote[];
  notesLastFetchedReportId: string | null;
  
  // Documents
  documents: ParsedCOSODocument[];
  documentsLastFetchedReportId: string | null;
  
  // Loading states
  isLoadingNotes: boolean;
  isLoadingDocuments: boolean;
}

// ========================================
// Initial State
// ========================================

const initialState: COSOComplianceState = {
  // Notes
  notes: [],
  notesLastFetchedReportId: null,
  
  // Documents
  documents: [],
  documentsLastFetchedReportId: null,
  
  // Loading states
  isLoadingNotes: false,
  isLoadingDocuments: false,
};

// ========================================
// Slice
// ========================================

const cosoComplianceSlice = createSlice({
  name: 'cosoCompliance',
  initialState,
  reducers: {
    // ========== Notes Actions ==========
    
    setNotes: (state, action: PayloadAction<{ notes: ParsedCOSONote[]; reportId: string }>) => {
      state.notes = action.payload.notes;
      state.notesLastFetchedReportId = action.payload.reportId;
    },
    
    updateNote: (state, action: PayloadAction<{ principleNumber: number; content: string }>) => {
      const { principleNumber, content } = action.payload;
      const existingNoteIndex = state.notes.findIndex(n => n.principleNumber === principleNumber);
      
      if (existingNoteIndex >= 0) {
        // Update existing note
        state.notes[existingNoteIndex].content = content;
      } else {
        // Add new note
        state.notes.push({
          id: '', // Will be assigned by server
          principleNumber,
          content,
        });
      }
    },
    
    setLoadingNotes: (state, action: PayloadAction<boolean>) => {
      state.isLoadingNotes = action.payload;
    },
    
    // ========== Documents Actions ==========
    
    setDocuments: (state, action: PayloadAction<{ documents: ParsedCOSODocument[]; reportId: string }>) => {
      state.documents = action.payload.documents;
      state.documentsLastFetchedReportId = action.payload.reportId;
    },
    
    addDocument: (state, action: PayloadAction<ParsedCOSODocument>) => {
      state.documents.unshift(action.payload);
    },
    
    removeDocument: (state, action: PayloadAction<string>) => {
      state.documents = state.documents.filter(d => d.id !== action.payload);
    },
    
    setLoadingDocuments: (state, action: PayloadAction<boolean>) => {
      state.isLoadingDocuments = action.payload;
    },
    
    // ========== Clear State ==========
    
    clearCOSOComplianceData: (state) => {
      state.notes = [];
      state.notesLastFetchedReportId = null;
      state.documents = [];
      state.documentsLastFetchedReportId = null;
    },
  },
  
  extraReducers: (builder) => {
    builder.addCase(resetReduxState, () => initialState);
  },
});

// ========================================
// Export Actions
// ========================================

export const {
  // Notes
  setNotes,
  updateNote,
  setLoadingNotes,
  
  // Documents
  setDocuments,
  addDocument,
  removeDocument,
  setLoadingDocuments,
  
  // Clear
  clearCOSOComplianceData,
} = cosoComplianceSlice.actions;

// ========================================
// Selectors
// ========================================

/**
 * Get content for a specific principle number
 */
export const selectNoteByPrincipleNumber = (
  state: { cosoCompliance: COSOComplianceState },
  principleNumber: number
): string | null => {
  const note = state.cosoCompliance.notes.find(n => n.principleNumber === principleNumber);
  return note?.content || null;
};

// ========================================
// Export Reducer
// ========================================

export default cosoComplianceSlice.reducer;

